import type { Route } from "./+types/contact-us";

import {
  ArrowRightIcon,
  GiftIcon,
  InstagramIcon,
  MailIcon,
  MessageCircleIcon,
  PhoneIcon,
} from "lucide-react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { data } from "react-router";

import { Button } from "~/core/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/core/components/ui/card";
import i18next from "~/core/lib/i18next.server";
import { KakaoLogo } from "~/features/auth/components/logos/kakao";

export const meta: Route.MetaFunction = ({ data }) => {
  return [
    { title: data?.title },
    { name: "description", content: data?.description },
  ];
};

export async function loader({ request }: Route.LoaderArgs) {
  const t = await i18next.getFixedT(request);
  return data(
    {
      title: `${t("contactPage.metaTitle")} | ${import.meta.env.VITE_APP_NAME}`,
      description: t("contactPage.metaDescription"),
    },
    {
      headers: {
        "Cache-Control": "private, max-age=60, stale-while-revalidate=300",
      },
    },
  );
}

const CONTACT_LINKS = {
  phoneDisplay: "0507-1348-2785",
  phoneHref: "tel:050713482785",
  kakaoConsultationHref: "https://open.kakao.com/o/consultation",
  kakaoTrialHref: "https://open.kakao.com/o/free-trial",
  instagramHref:
    "https://www.instagram.com/ycu_wrestling?igsh=MWk0dTE5azBiZmpmaA==",
  whatsappHref: "https://wa.me/821000000000",
  emailDisplay: "coach@ycu-wrestling.example",
  emailHref: "mailto:coach@ycu-wrestling.example",
} as const;

type MethodId = "phone" | "kakao" | "instagram" | "whatsapp" | "email";

const METHOD_ORDER: MethodId[] = [
  "phone",
  "kakao",
  "instagram",
  "whatsapp",
  "email",
];

export default function ContactUs() {
  const { t } = useTranslation();

  const contactMethods = useMemo(
    () =>
      METHOD_ORDER.map((id) => {
        const base = {
          id,
          title: t(`contactPage.${id}Title`),
          description: t(`contactPage.${id}Description`),
          cta: t(`contactPage.${id}Cta`),
        };
        switch (id) {
          case "phone":
            return {
              ...base,
              Icon: PhoneIcon,
              value: CONTACT_LINKS.phoneDisplay,
              href: CONTACT_LINKS.phoneHref,
            };
          case "kakao":
            return {
              ...base,
              Icon: KakaoLogo,
              value: t("contactPage.kakaoValue"),
              href: CONTACT_LINKS.kakaoConsultationHref,
            };
          case "instagram":
            return {
              ...base,
              Icon: InstagramIcon,
              value: t("contactPage.instagramValue"),
              href: CONTACT_LINKS.instagramHref,
            };
          case "whatsapp":
            return {
              ...base,
              Icon: MessageCircleIcon,
              value: CONTACT_LINKS.phoneDisplay,
              href: CONTACT_LINKS.whatsappHref,
            };
          case "email":
            return {
              ...base,
              Icon: MailIcon,
              value: CONTACT_LINKS.emailDisplay,
              href: CONTACT_LINKS.emailHref,
            };
        }
      }),
    [t],
  );

  return (
    <main className="mx-auto w-full max-w-6xl px-4 py-10 md:py-14">
      <section className="overflow-hidden rounded-3xl border bg-gradient-to-br from-amber-50 via-background to-red-50 p-4 dark:from-amber-950/20 dark:to-red-950/20 sm:p-5 md:p-10">
        <div className="grid gap-6 md:grid-cols-[1.1fr_0.9fr] md:items-center md:gap-8">
          <div className="min-w-0">
            <span className="bg-background/80 text-primary inline-flex rounded-full border px-3 py-1 text-xs font-semibold tracking-widest uppercase">
              {t("contactPage.eyebrow")}
            </span>
            <h1 className="mt-4 break-words text-3xl leading-tight font-extrabold tracking-tight sm:text-4xl md:text-5xl">
              {t("contactPage.heroTitle")}
            </h1>
            <p className="text-muted-foreground mt-4 max-w-2xl break-words text-sm leading-6 whitespace-pre-line sm:text-base md:text-lg">
              {t("contactPage.heroDescription")}
            </p>
          </div>

          <Card className="bg-background/90 border-primary/20 min-w-0">
            <CardHeader>
              <div className="bg-primary/10 text-primary mb-2 flex size-12 items-center justify-center rounded-xl">
                <GiftIcon className="size-6" aria-hidden />
              </div>
              <CardTitle className="break-words text-xl sm:text-2xl">
                {t("contactPage.trialCardTitle")}
              </CardTitle>
              <CardDescription className="break-words text-sm leading-6 sm:text-base">
                {t("contactPage.trialCardDescription")}
              </CardDescription>
            </CardHeader>
            <CardContent className="min-w-0">
              <Button
                asChild
                size="lg"
                className="h-auto w-full min-w-0 whitespace-normal py-2 text-center leading-snug"
              >
                <a
                  href={CONTACT_LINKS.kakaoTrialHref}
                  target="_blank"
                  rel="noreferrer"
                >
                  {t("contactPage.trialCardCta")}
                  <ArrowRightIcon className="ml-2 size-4" aria-hidden />
                </a>
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      <section className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {contactMethods.map(
          ({ id, title, description, value, href, cta, Icon }) => (
            <Card
              key={id}
              className="hover:border-primary/50 h-full min-w-0 transition-colors"
            >
              <CardHeader>
                <div className="bg-primary/10 text-primary mb-2 flex size-11 items-center justify-center rounded-lg">
                  <Icon className="size-5" aria-hidden />
                </div>
                <CardTitle className="break-words">{title}</CardTitle>
                <CardDescription className="break-words text-sm leading-6">
                  {description}
                </CardDescription>
              </CardHeader>
              <CardContent className="mt-auto flex min-w-0 flex-col gap-4">
                <div className="break-all text-sm font-medium">{value}</div>
                <Button
                  asChild
                  variant="outline"
                  className="h-auto w-full min-w-0 whitespace-normal py-2 text-center leading-snug"
                >
                  <a
                    href={href}
                    target={href.startsWith("http") ? "_blank" : undefined}
                    rel={href.startsWith("http") ? "noreferrer" : undefined}
                  >
                    {cta}
                    <ArrowRightIcon className="ml-2 size-4" aria-hidden />
                  </a>
                </Button>
              </CardContent>
            </Card>
          ),
        )}
      </section>

      <section className="mt-8 rounded-2xl border p-4 sm:p-5">
        <h2 className="text-lg font-bold">{t("contactPage.noteTitle")}</h2>
        <p className="text-muted-foreground mt-2 break-words text-sm leading-6 whitespace-pre-line">
          {t("contactPage.noteDescription")}
        </p>
      </section>
    </main>
  );
}
